/**
 * MSSQL built-in AGGREGATE + WINDOW/RANKING function signatures — one family
 * slice of the static catalog composed by `mssqlBuiltinRoutines.ts` (see
 * that file's header for why this exists and the precedence rule vs. user
 * routines).
 *
 * Ranking functions that take zero call-parens arguments (`RANK()`,
 * `ROW_NUMBER()`, `DENSE_RANK()`, `PERCENT_RANK()`, `CUME_DIST()` — all of
 * their configuration lives in the trailing `OVER (...)` clause, not the
 * call itself) are excluded — see `MSSQL_EXCLUDED_FROM_SIGNATURE_HELP` in
 * the barrel.
 */

import type { RoutineMetadata, RoutineParameter } from '../../../../types/electron';

type Entry = RoutineMetadata & { documentation?: string };

function p(name: string, dataType: string): RoutineParameter {
  return { name, dataType };
}

function opt(name: string, dataType: string): RoutineParameter {
  return { name: `[${name}]`, dataType };
}

/** Variadic trailing parameter — mirrors `sqliteBuiltinRoutines.ts`'s `printf(FORMAT, ...)` convention. */
function variadic(dataType: string): RoutineParameter {
  return { name: '...', dataType };
}

function entry(name: string, parameters: RoutineParameter[], documentation?: string): Entry {
  return { schema: '', name, parameters, ...(documentation ? { documentation } : {}) };
}

export const MSSQL_AGGREGATE_ROUTINES: ReadonlyMap<string, Entry> = new Map([
  // --- Aggregate ---
  ['approx_count_distinct', entry('APPROX_COUNT_DISTINCT', [p('expression', 'any')], 'Return an approximate count of distinct non-NULL values.')],
  ['avg', entry('AVG', [p('expression', 'numeric')], 'Return the average of non-NULL values in a group.')],
  ['checksum_agg', entry('CHECKSUM_AGG', [p('expression', 'int')], 'Return a checksum of the values in a group; order-independent.')],
  ['count', entry('COUNT', [p('* | expression', 'any')], 'Return the number of rows, or of non-NULL values of an expression.')],
  ['count_big', entry('COUNT_BIG', [p('* | expression', 'any')], 'Like COUNT but returns bigint for very large groups.')],
  ['grouping', entry('GROUPING', [p('column_expression', 'any')], 'Return 1 if a column is aggregated by ROLLUP/CUBE/GROUPING SETS, else 0.')],
  ['grouping_id', entry('GROUPING_ID', [p('column_expression', 'any'), variadic('any')], 'Return a bitmask identifying the grouping level of a row (variadic — one or more column expressions).')],
  ['max', entry('MAX', [p('expression', 'any')], 'Return the maximum value in a group.')],
  ['min', entry('MIN', [p('expression', 'any')], 'Return the minimum value in a group.')],
  ['stdev', entry('STDEV', [p('expression', 'numeric')], 'Return the statistical standard deviation of all values in a group.')],
  ['stdevp', entry('STDEVP', [p('expression', 'numeric')], 'Return the statistical standard deviation for the population.')],
  ['string_agg', entry('STRING_AGG', [p('expression', 'varchar'), p('separator', 'varchar')], 'Concatenate non-NULL values of a group with a separator (WITHIN GROUP for order).')],
  ['sum', entry('SUM', [p('expression', 'numeric')], 'Return the sum of non-NULL values in a group.')],
  ['var', entry('VAR', [p('expression', 'numeric')], 'Return the statistical variance of all values in a group.')],
  ['varp', entry('VARP', [p('expression', 'numeric')], 'Return the statistical variance for the population.')],
  // --- Window / ranking ---
  ['first_value', entry('FIRST_VALUE', [p('expression', 'any')], 'Return the first value in an ordered window frame.')],
  ['lag', entry('LAG', [p('scalar_expression', 'any'), opt('offset', 'int'), opt('default', 'any')], 'Return a value from a prior row in the window ordering.')],
  ['last_value', entry('LAST_VALUE', [p('expression', 'any')], 'Return the last value in an ordered window frame.')],
  ['lead', entry('LEAD', [p('scalar_expression', 'any'), opt('offset', 'int'), opt('default', 'any')], 'Return a value from a following row in the window ordering.')],
  ['ntile', entry('NTILE', [p('integer_expression', 'int')], 'Distribute rows into a specified number of roughly equal groups.')],
  ['percentile_cont', entry('PERCENTILE_CONT', [p('numeric_literal', 'float')], 'Interpolated percentile value (WITHIN GROUP (ORDER BY ...)).')],
  ['percentile_disc', entry('PERCENTILE_DISC', [p('numeric_literal', 'float')], 'Discrete percentile value (WITHIN GROUP (ORDER BY ...)).')],
]);
